import { useEffect, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { useVisibleSections } from "@/content/ContentContext";
import { TYPE_VIEWS } from "./SectionViews";
import { useT } from "@/content/i18n";

const ease = [0.22, 1, 0.36, 1];

export const Panel = ({ activeId, isMobile, onClose, onSelect, openLightbox }) => {
  const scroller = useRef(null);
  const sections = useVisibleSections();
  const t = useT();
  const ORDER = sections.map((s) => s.id);
  const section = sections.find((s) => s.id === activeId);
  const idx = ORDER.indexOf(activeId);
  const go = (d) => onSelect(ORDER[(idx + d + ORDER.length) % ORDER.length]);

  useEffect(() => {
    if (scroller.current) scroller.current.scrollTop = 0;
  }, [activeId]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const View = TYPE_VIEWS[section?.type];
  const motionProps = isMobile
    ? { initial: { y: "100%" }, animate: { y: 0 }, exit: { y: "100%" } }
    : { initial: { x: 60, opacity: 0 }, animate: { x: 0, opacity: 1 }, exit: { x: 60, opacity: 0 } };

  return (
    <AnimatePresence>
      {section && (
        <motion.aside
          key="panel"
          data-testid="detail-drawer-container"
          className="panel fixed z-50 flex flex-col"
          {...motionProps}
          transition={{ duration: 0.7, ease }}
        >
          <div className="flex items-start justify-between gap-4 border-b border-[#F7F4EE]/10 px-6 pb-5 pt-6 sm:px-8">
            <div className="min-w-0">
              <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#E69C24]">
                N° {String(idx + 1).padStart(2, "0")} / {ORDER.length}{section?.object ? ` — ${section.object}` : ""}
              </p>
              <AnimatePresence initial={false}>
                <motion.h2
                  key={activeId}
                  data-testid="detail-drawer-title"
                  className="mt-2 font-display text-2xl leading-tight text-[#F7F4EE] sm:text-3xl"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, ease }}
                >
                  {section?.label}
                </motion.h2>
              </AnimatePresence>
            </div>
            <div className="flex shrink-0 items-center gap-1.5">
              <button data-testid="detail-prev-button" onClick={() => go(-1)} className="icon-btn" aria-label={t("prev")}><ChevronLeft className="h-4 w-4" /></button>
              <button data-testid="detail-next-button" onClick={() => go(1)} className="icon-btn" aria-label={t("next")}><ChevronRight className="h-4 w-4" /></button>
              <button data-testid="detail-drawer-close-button" onClick={onClose} className="icon-btn is-close" aria-label={t("close")}><X className="h-4 w-4" /></button>
            </div>
          </div>
          <div ref={scroller} className="panel-scroll flex-1 overflow-y-auto overscroll-contain px-6 py-6 sm:px-8">
            <motion.div
              key={activeId}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1, ease }}
            >
              {View ? <View data={section?.data} openLightbox={openLightbox} scroller={scroller} /> : null}
            </motion.div>
          </div>
        </motion.aside>
      )}
    </AnimatePresence>
  );
};
